import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import { api } from "@/lib/api";
import { Plus, Edit3, Trash2, Folder, FolderOpen, Archive, Users, Loader2, X, Check } from "lucide-react";
import { toast } from "sonner";

const PALETTE = ["#0A192F", "#B7955B", "#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#06B6D4", "#EC4899", "#84CC16"];

export default function PortfoliosPage() {
  const [items, setItems] = useState([]);
  const [unassigned, setUnassigned] = useState(0);
  const [totalAssigned, setTotalAssigned] = useState(0);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);   // portfolio in fase di edit
  const [creating, setCreating] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/portfolios");
      setItems(r.data.items || []);
      setUnassigned(r.data.unassigned_count || 0);
      setTotalAssigned(r.data.total_assigned || 0);
    } catch { toast.error("Errore caricamento portafogli"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onSave = async (patch) => {
    try {
      if (editing?.__new) {
        await api.post("/portfolios", patch);
        toast.success("Portafoglio creato");
      } else if (editing) {
        await api.patch(`/portfolios/${editing.id}`, patch);
        toast.success("Portafoglio aggiornato");
      }
      setEditing(null);
      setCreating(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore salvataggio"); }
  };

  const onDelete = async (p) => {
    if (!window.confirm(`Eliminare il portafoglio "${p.name}"?\n\nI ${p.client_count || 0} clienti NON vengono eliminati: vengono solo scollegati dal portafoglio.`)) return;
    try {
      await api.delete(`/portfolios/${p.id}?detach_clients=true`);
      toast.success("Portafoglio eliminato");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  return (
    <AppLayout
      title="Portafogli"
      subtitle="Diversificazione operativa"
      actions={
        <button
          onClick={() => { setCreating(true); setEditing({ __new: true, name: "", color: PALETTE[items.length % PALETTE.length], description: "" }); }}
          data-testid="btn-new-portfolio"
          className="text-white text-sm px-4 py-2 rounded-[3px] flex items-center gap-2 transition" style={{ background: "var(--navy)" }} onMouseEnter={(e)=>e.currentTarget.style.background="var(--navy-3)"} onMouseLeave={(e)=>e.currentTarget.style.background="var(--navy)"} 
        >
          <Plus className="w-4 h-4" /> Nuovo Portafoglio
        </button>
      }
    >
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <KpiCard label="Portafogli attivi" value={items.filter(i => !i.archived).length} icon={Folder} accent="#0A192F" />
        <KpiCard label="Clienti assegnati" value={totalAssigned} icon={Users} accent="#B7955B" />
        <KpiCard label="Senza portafoglio" value={unassigned} icon={Archive} accent="#94a3b8" />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12 text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-[3px] p-12 text-center text-slate-500">
          Nessun portafoglio creato. I portafogli si creano automaticamente quando importi un file Excel,
          oppure puoi crearli manualmente con il pulsante in alto.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {items.map((p) => (
            <PortfolioCard
              key={p.id}
              p={p}
              onOpen={() => nav(`/clienti?portfolio=${p.id}`)}
              onEdit={() => setEditing(p)}
              onDelete={() => onDelete(p)}
            />
          ))}
        </div>
      )}

      {(editing && (creating || !editing.__new)) && (
        <PortfolioDialog
          initial={editing}
          isNew={!!editing.__new}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSave={onSave}
        />
      )}
    </AppLayout>
  );
}

function KpiCard({ label, value, icon: Icon, accent }) {
  return (
    <div className="bg-white border border-slate-200 rounded-[3px] p-4 flex items-center gap-3" data-testid={`kpi-${label}`}>
      <div className="w-10 h-10 rounded-[3px] flex items-center justify-center" style={{ background: `${accent}15`, color: accent }}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">{label}</div>
        <div className="text-2xl font-serif text-slate-900 tabular-nums">{value}</div>
      </div>
    </div>
  );
}

function PortfolioCard({ p, onOpen, onEdit, onDelete }) {
  return (
    <div
      className="bg-white border border-slate-200 rounded-[3px] overflow-hidden hover:shadow-md transition group"
      data-testid={`portfolio-card-${p.id}`}
    >
      <div className="h-2" style={{ background: p.color || "#B7955B" }} />
      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <FolderOpen className="w-5 h-5 shrink-0" style={{ color: p.color || "#B7955B" }} />
            <h3 className="font-serif text-lg text-slate-900 truncate">{p.name}</h3>
          </div>
          {p.archived && <span className="text-[9px] uppercase tracking-wider bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded-[3px]">Archiv.</span>}
        </div>
        {p.description && <div className="text-xs text-slate-500 mt-1 line-clamp-2">{p.description}</div>}
        <div className="flex items-baseline gap-1.5 mt-3">
          <span className="text-3xl font-serif tabular-nums" style={{ color: p.color || "#0A192F" }}>{p.client_count || 0}</span>
          <span className="text-xs text-slate-500">clienti</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-400 mt-1">
          <span className="uppercase tracking-wider">{p.source || "manual"}</span>
          {p.created_at && <span>· {new Date(p.created_at).toLocaleDateString("it-IT")}</span>}
        </div>
        <div className="flex items-center gap-1.5 mt-4 pt-3 border-t border-[#F0F2F5]">
          <button
            onClick={onOpen}
            data-testid={`btn-open-${p.id}`}
            className="flex-1 text-xs px-3 py-1.5 bg-[#0A192F] text-white rounded-[3px] hover:bg-[#172A45]"
          >
            Apri clienti
          </button>
          <button onClick={onEdit} data-testid={`btn-edit-${p.id}`} className="p-1.5 text-slate-500 hover:text-[#0A192F] rounded-[3px] hover:bg-slate-100">
            <Edit3 className="w-3.5 h-3.5" />
          </button>
          <button onClick={onDelete} data-testid={`btn-del-${p.id}`} className="p-1.5 text-rose-500 hover:text-rose-700 rounded-[3px] hover:bg-rose-50">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

function PortfolioDialog({ initial, isNew, onClose, onSave }) {
  const [name, setName] = useState(initial.name || "");
  const [color, setColor] = useState(initial.color || PALETTE[0]);
  const [description, setDescription] = useState(initial.description || "");
  const [archived, setArchived] = useState(!!initial.archived);

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="portfolio-dialog">
      <div className="bg-white rounded-[3px] shadow-xl w-full max-w-md">
        <div className="border-b border-slate-200 px-5 py-3 flex items-center justify-between">
          <h2 className="font-serif text-lg">{isNew ? "Nuovo Portafoglio" : "Modifica Portafoglio"}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500">Nome</label>
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="portfolio-name"
              className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-[3px] focus:border-[#0A192F] focus:ring-1 focus:ring-[#0A192F]/20"
              placeholder="Es: OPEN FINANCE"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500">Colore</label>
            <div className="flex flex-wrap gap-2 mt-1">
              {PALETTE.map((c) => (
                <button
                  key={c}
                  onClick={() => setColor(c)}
                  data-testid={`color-${c}`}
                  className="w-8 h-8 rounded-[3px] relative"
                  style={{ background: c }}
                >
                  {color === c && <Check className="w-4 h-4 text-white absolute inset-0 m-auto" />}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500">Descrizione (opz.)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              data-testid="portfolio-desc"
              className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-[3px] focus:border-[#0A192F] focus:ring-1 focus:ring-[#0A192F]/20"
            />
          </div>
          {!isNew && (
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={archived} onChange={(e) => setArchived(e.target.checked)} />
              Archivia portafoglio
            </label>
          )}
        </div>
        <div className="border-t border-slate-200 px-5 py-3 flex items-center justify-end gap-2">
          <button onClick={onClose} className="text-sm px-4 py-2 text-slate-600 hover:text-slate-900">Annulla</button>
          <button
            onClick={() => {
              if (!name.trim()) return toast.error("Inserisci un nome");
              onSave({ name: name.trim(), color, description: description.trim(), ...(isNew ? {} : { archived }) });
            }}
            data-testid="portfolio-save"
            className="text-sm px-4 py-2 bg-[#0A192F] text-white rounded-[3px] hover:bg-[#172A45]"
          >
            {isNew ? "Crea" : "Salva"}
          </button>
        </div>
      </div>
    </div>
  );
}
